import type {
  ExecutionContext,
  InvoiceDetail,
  InvoiceFilter,
  InvoiceRepository,
  InvoiceStatus,
  InvoiceSummary,
  InvoiceType,
  Payment,
  RecordPaymentCommand,
} from "@dker/core";
import { DomainError, computeBalance } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

const num = (v: unknown) => Number(v ?? 0);

const SUMMARY_COLUMNS = `
  id, invoice_number, type, title, status, due_date,
  total_ht, total_tva, total_ttc, amount_paid,
  prior_invoiced_ht, prior_invoiced_ttc,
  einvoice_status, einvoice_submitted_at,
  client:clients(id, first_name, last_name, company_name, email)
`;

function toSummary(row: Row): InvoiceSummary {
  const client = Array.isArray(row.client) ? row.client[0] : row.client;
  const base = {
    id: row.id,
    invoiceNumber: row.invoice_number ?? "",
    type: (row.type ?? "invoice") as InvoiceType,
    title: row.title,
    status: row.status as InvoiceStatus,
    client: client
      ? {
          id: client.id,
          firstName: client.first_name ?? null,
          lastName: client.last_name ?? null,
          companyName: client.company_name ?? null,
          email: client.email ?? null,
        }
      : null,
    totalHt: num(row.total_ht),
    totalTva: num(row.total_tva),
    totalTtc: num(row.total_ttc),
    amountPaid: num(row.amount_paid),
    priorInvoicedHt: num(row.prior_invoiced_ht),
    priorInvoicedTtc: num(row.prior_invoiced_ttc),
    dueDate: row.due_date,
    einvoiceStatus: (row.einvoice_status ?? "not_sent") as InvoiceSummary["einvoiceStatus"],
    einvoiceSubmittedAt: row.einvoice_submitted_at ?? null,
  };
  return { ...base, balance: computeBalance(base) };
}

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabaseInvoiceRepository(
  clientFor: DbClientFactory,
): InvoiceRepository {
  return {
    async list(ctx: ExecutionContext, filter?: InvoiceFilter): Promise<InvoiceSummary[]> {
      let query = clientFor(ctx)
        .from("invoices")
        .select(SUMMARY_COLUMNS)
        .order("due_date", { ascending: false });

      if (filter?.status?.length) query = query.in("status", filter.status);
      if (filter?.clientId) query = query.eq("client_id", filter.clientId);

      const { data, error } = await query;
      if (error) fail(error);
      return (data ?? []).map(toSummary);
    },

    async findById(ctx: ExecutionContext, invoiceId: string): Promise<InvoiceDetail | null> {
      const db = clientFor(ctx);

      const [invoice, payments, reminders] = await Promise.all([
        db
          .from("invoices")
          .select(
            `${SUMMARY_COLUMNS}, quote_id, project_id, deposit_percent, situation_percent,
             quote:quotes(quote_number, total_ttc)`,
          )
          .eq("id", invoiceId)
          .maybeSingle(),
        db
          .from("payments")
          .select("id, amount, payment_date, payment_method, reference, notes")
          .eq("invoice_id", invoiceId)
          .order("payment_date", { ascending: false }),
        db
          .from("reminder_actions")
          .select("id, channel, status, scheduled_at, sent_at, subject")
          .eq("invoice_id", invoiceId)
          .order("scheduled_at", { ascending: false }),
      ]);

      if (invoice.error) fail(invoice.error);
      if (!invoice.data) return null;

      const quoteId: string | null = invoice.data.quote_id ?? null;

      // Le detail facture vient du devis : il n'existe pas de table de lignes
      // de facture, et il n'en faut pas — une facture de travaux facture le
      // marche, et le marche est le devis. Une seconde requete plutot qu'une
      // jointure imbriquee, pour que l'ordre d'affichage soit garanti.
      const lignes = quoteId
        ? await db
            .from("quote_lines")
            .select(
              "id, is_section_header, section_title, designation, unit, quantity, unit_cost, unit_price, tva_rate, notes",
            )
            .eq("quote_id", quoteId)
            .order("sort_order", { ascending: true })
        : null;
      if (lignes?.error) fail(lignes.error);

      const devis = Array.isArray(invoice.data.quote)
        ? invoice.data.quote[0]
        : invoice.data.quote;

      // Un seul pourcentage, dont le sens vient du type. Deux colonnes en base
      // parce que la contrainte les distingue ; un seul champ ici parce qu'une
      // facture n'est jamais les deux a la fois.
      const percent =
        invoice.data.type === "deposit"
          ? invoice.data.deposit_percent
          : invoice.data.type === "situation"
            ? invoice.data.situation_percent
            : null;

      return {
        ...toSummary(invoice.data),
        quoteId,
        projectId: invoice.data.project_id ?? null,
        lines: (lignes?.data ?? []).map((l: Row) => ({
          id: l.id,
          isSectionHeader: l.is_section_header ?? false,
          sectionTitle: l.section_title ?? null,
          designation: l.designation ?? "",
          unit: l.unit ?? "u",
          quantity: num(l.quantity),
          unitCost: num(l.unit_cost),
          unitPrice: num(l.unit_price),
          tvaRate: num(l.tva_rate),
          notes: l.notes ?? null,
        })),
        quoteNumber: devis?.quote_number ?? null,
        quoteTotalTtc: devis?.total_ttc == null ? null : num(devis.total_ttc),
        percent: percent == null ? null : num(percent),
        payments: (payments.data ?? []).map((p: Row) => ({
          id: p.id,
          amount: num(p.amount),
          paymentDate: p.payment_date,
          paymentMethod: p.payment_method ?? null,
          reference: p.reference ?? null,
          notes: p.notes ?? null,
        })),
        reminders: (reminders.data ?? []).map((r: Row) => ({
          id: r.id,
          channel: r.channel,
          status: r.status,
          scheduledAt: r.scheduled_at,
          sentAt: r.sent_at ?? null,
          subject: r.subject ?? null,
        })),
      };
    },

    async recordPayment(
      ctx: ExecutionContext,
      command: RecordPaymentCommand,
    ): Promise<Payment> {
      const { data, error } = await clientFor(ctx)
        .from("payments")
        .insert({
          invoice_id: command.invoiceId,
          amount: command.amount,
          payment_date: command.paymentDate,
          payment_method: command.paymentMethod,
          reference: command.reference,
          notes: command.notes,
        })
        .select()
        .single();

      if (error) fail(error);

      // `invoices.amount_paid` et le statut sont recalculés par trigger : on
      // ne les touche pas ici, sinon deux écritures concurrentes se
      // marcheraient dessus.
      return {
        id: data.id,
        amount: num(data.amount),
        paymentDate: data.payment_date,
        paymentMethod: data.payment_method ?? null,
        reference: data.reference ?? null,
        notes: data.notes ?? null,
      };
    },
  };
}
